import { useEffect, useState } from "react";
import FractalShader from "./components/FractalShader";
import ParticleBurstText from "./components/ParticleBurstText";
import HorizontalGallery from "./components/HorizontalGallery";
import SketchbookReveal from "./components/SketchbookReveal";
import StoryChapter from "./components/StoryChapter";
import BookAppointment from "./components/BookAppointment";
import StudioFooter from "./components/StudioFooter";

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const smoothstep = (a: number, b: number, x: number) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1440,
    h: typeof window !== "undefined" ? window.innerHeight : 900,
  }));

  useEffect(() => {
    // rAF-throttle scroll updates so React re-renders align with the browser's
    // paint cadence. Without this, scroll fires far more frequently than 60fps
    // on many trackpads and each event triggers a full home-tree reconcile —
    // which was making the particle burst feel stuttery.
    let raf = 0;
    let pending = false;

    const flush = () => {
      pending = false;
      setScrollY(window.scrollY);
    };
    const onScroll = () => {
      if (pending) return;
      pending = true;
      raf = requestAnimationFrame(flush);
    };
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });

    setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  const { w: vw, h: vh } = viewport;

  // ---- Scroll timeline (in viewport heights) ----
  //   0 → 1vh     : hero
  //   0.6 → 1.6vh : light orb glides to center + scales up 3×
  //   2   → 2.6vh : orb fades out
  //   2.35 → 2.9vh: philosophy headline reveals
  //   3.2 → 3.9vh : philosophy headline particle-bursts apart  (all within section 3 pin, which runs 2.0 → 4.0vh)
  //   4.3 → 5.1vh : full-bleed lighting image emerges from the particle burst
  //   5.0 → 6.5vh : image chapter holds pinned; slow parallax + subtle zoom-out
  //   6.2 → 6.6vh : horizontal project gallery slides in
  //   6.5 → 8.0vh : horizontal project gallery sweeps left (pinned)
  //   8.9 → 9.25vh: whole stage transitions dark → paper (tone) — tightened for a snappier, more elegant handoff
  //   9.5  → 12.1vh: sketches draw themselves in
  const t2 = smoothstep(0, 1, clamp01((scrollY - 0.6 * vh) / vh));
  const tFade = clamp01((scrollY - 2 * vh) / (0.6 * vh));
  const tText = smoothstep(
    0,
    1,
    clamp01((scrollY - 2.35 * vh) / (0.55 * vh))
  );
  const tBurst = clamp01((scrollY - 3.2 * vh) / (0.7 * vh));

  // Full-bleed image chapter — emerges as the burst completes, then holds
  // pinned while you scroll through it with a slow parallax + zoom-out.
  const tImageIn = smoothstep(
    0,
    1,
    clamp01((scrollY - 4.3 * vh) / (0.8 * vh))
  );
  const tImagePan = clamp01((scrollY - 5.0 * vh) / (1.5 * vh));

  // Gallery entrance + horizontal sweep (shifted +1.5vh for new image section)
  const tGalleryEnter = smoothstep(
    0,
    1,
    clamp01((scrollY - 6.2 * vh) / (0.4 * vh))
  );
  // Sweep runs from y=650vh to y=800vh — exactly the pin runway of section 4
  // (section height 250vh minus one viewport). This means the last card lands
  // right as the pin releases, so there's no motionless dead scroll after it.
  const tGallerySweep = smoothstep(
    0,
    1,
    clamp01((scrollY - 6.5 * vh) / (1.5 * vh))
  );

  // Dark → light tone transition begins as section 5 comes into view.
  // Kept short (0.35vh) so the paper reveal feels crisp rather than dragging.
  const tTone = smoothstep(
    0,
    1,
    clamp01((scrollY - 8.9 * vh) / (0.35 * vh))
  );
  // Sketches start noticeably later, giving the headline real room to breathe.
  // Span is wide enough that (a) the grid fades in, (b) the intro drifts off,
  // and (c) the grid pans upward to reveal its bottom rows — all before
  // section 5's pin releases.
  const tSketch = smoothstep(
    0,
    1,
    clamp01((scrollY - 9.5 * vh) / (2.6 * vh))
  );

  // Palette progress: cycles through the shader palette waypoints.
  const scrollProgress = clamp01(scrollY / (3 * vh));

  // ---- Orb layer transform ----
  // On mobile (<1024px) the orb container is anchored to the BOTTOM HALF of
  // the screen (full width, 50vh tall) so it sits under the hero text.
  // On desktop it's anchored to the RIGHT HALF (half width, full height).
  // The base/center offsets below are tuned per-breakpoint so the composition
  // reads well in both orientations.
  const isMobile = vw < 1024;
  const baseX = isMobile ? 0 : 64;
  const baseY = isMobile ? 0 : -112;
  const baseScale = isMobile ? 1.15 : 1.21;
  const centerX = isMobile ? 0 : -vw / 4;
  const centerY = isMobile ? -vh / 4 : 0;
  const centerScale = 3.0;

  const tx = lerp(baseX, centerX, t2);
  const ty = lerp(baseY, centerY, t2);
  const scale = lerp(baseScale, centerScale, t2);
  const orbOpacity = 1 - tFade;
  const orbHidden = orbOpacity < 0.01;

  // Finale headline stays visible until burst begins; particles handle the fade after.
  const headlineOpacity = tText * (1 - Math.max(0, (tBurst - 0.5) / 0.5));

  // Header ink shifts from white → graphite as we cross into the sketchbook.
  const headerR = Math.round(lerp(255, 27, tTone));
  const headerG = Math.round(lerp(255, 24, tTone));
  const headerB = Math.round(lerp(255, 21, tTone));
  const headerInk = `rgb(${headerR}, ${headerG}, ${headerB})`;

  // Global page background lerps too so any gap between sections stays seamless.
  const pageR = Math.round(lerp(0, 245, tTone));
  const pageG = Math.round(lerp(0, 239, tTone));
  const pageB = Math.round(lerp(0, 228, tTone));
  const pageBg = `rgb(${pageR}, ${pageG}, ${pageB})`;

  return (
    <div
      className="relative"
      style={{
        fontFamily: "'Space Grotesk', 'Inter', system-ui, sans-serif",
        backgroundColor: pageBg,
        color: headerInk,
        transition: "background-color 60ms linear",
      }}
    >
      {/* Fixed light-orb layer.
          Desktop: anchored to the right half of the viewport.
          Mobile:  anchored to the bottom half so it sits UNDER the hero text
                   instead of behind it. The hero grid below mirrors this by
                   confining text to the top half on mobile. */}
      <div
        className="fixed inset-x-0 bottom-0 h-[50vh] w-full lg:inset-auto lg:top-0 lg:right-0 lg:h-screen lg:w-1/2 z-10 pointer-events-none"
        style={{
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transformOrigin: "center",
          opacity: orbOpacity,
          visibility: orbHidden ? "hidden" : "visible",
          willChange: "transform, opacity",
        }}
      >
        <div className="w-full h-full pointer-events-auto">
          <FractalShader scrollProgress={scrollProgress} />
        </div>
      </div>

      {/* Top bar */}
      <header
        className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-8 md:px-14 py-6"
        style={{ color: headerInk }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-2.5 h-2.5 rounded-full animate-pulse"
            style={{ backgroundColor: headerInk }}
          />
          <span
            className="tracking-[0.35em] text-[11px] uppercase whitespace-nowrap"
            style={{ color: headerInk, opacity: 0.9 }}
          >
            Elin Vahr · Lighting Design
          </span>
        </div>
        <a
          href="mailto:studio@elinvahr.com"
          className="text-[11px] uppercase tracking-[0.3em] transition-opacity hover:opacity-100 cursor-pointer whitespace-nowrap"
          style={{ color: headerInk, opacity: 0.7 }}
        >
          studio@elinvahr.com →
        </a>
      </header>

      {/* SECTION 1 — Hero.
          Desktop layout: 2 columns (text left, orb right).
          Mobile layout:  2 rows (text top, orb bottom) so the fixed orb layer
                          — which sits in the bottom half on mobile — never
                          overlaps the headline copy. */}
      <section className="relative h-screen w-full text-white">
        <main className="relative h-full w-full grid grid-cols-1 grid-rows-[1fr_1fr] lg:grid-cols-2 lg:grid-rows-1">
          <div
            className="relative z-20 flex flex-col justify-center lg:justify-center px-8 md:px-14 lg:px-20 pt-24 pb-6 lg:py-24"
            style={{ opacity: 1 - smoothstep(0.4, 0.95, scrollY / vh) }}
          >
            <div className="text-[10px] uppercase tracking-[0.4em] text-white/60 mb-8 flex items-center gap-3">
              <span className="w-8 h-px bg-white/60" />
              Portfolio · 2018 — 2025
            </div>

            <h1
              className="font-black uppercase leading-[0.85] tracking-[-0.03em] text-white"
              style={{ fontSize: "clamp(56px, 8vw, 140px)" }}
            >
              I DRAW
              <br />
              <span className="italic font-light lowercase tracking-tight text-white/70">
                with
              </span>{" "}
              LIGHT.
            </h1>

            <p className="mt-10 max-w-md text-sm md:text-base text-white/60 leading-relaxed">
              I&apos;m Elin — a lighting designer based between Copenhagen and
              Lisbon. For the last seven years I&apos;ve been shaping how
              hotels, restaurants, homes and quiet rooms feel after the sun
              goes down.
            </p>

          </div>
          <div />
        </main>
      </section>

      {/* SECTION 2 — Empty stage while orb flies to center + scales up. */}
      <section className="relative h-screen w-full text-white" />

      {/* SECTION 3 — Sticky finale: orb fades → headline reveals → headline bursts.
          Section is 300vh tall so its 200vh pin runway (scroll 2.0 → 4.0vh) fully
          contains the burst (3.2 → 3.9vh). The finale plays on-screen; no scroll-up
          needed to catch it. */}
      <section className="relative h-[300vh] w-full text-white">
        <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-visible">
          <ParticleBurstText
            progress={tBurst}
            className="w-full max-w-[1100px] px-8 text-center"
          >
            <div
              className="text-center"
              style={{
                opacity: headlineOpacity,
                transform: `translateY(${lerp(56, 0, tText)}px)`,
                willChange: "transform, opacity",
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.4em] text-white/60 mb-8 flex items-center gap-3 justify-center">
                <span className="w-8 h-px bg-white/60" />
                Chapter One · The Philosophy
                <span className="w-8 h-px bg-white/60" />
              </div>

              <h2
                className="font-black uppercase leading-[0.85] tracking-[-0.03em] text-white"
                style={{ fontSize: "clamp(64px, 10vw, 180px)" }}
              >
                A ROOM
                <br />
                <span className="italic font-light lowercase tracking-tight text-white/70">
                  remembers
                </span>{" "}
                LIGHT.
              </h2>

              <p className="mt-10 max-w-lg mx-auto text-sm md:text-base text-white/55 leading-relaxed">
                Before the furniture, before the color, before the guests
                arrive — a room already knows how it wants to be lit. My job
                is to listen for that, then quietly draw it out.
              </p>
            </div>
          </ParticleBurstText>
        </div>
      </section>

      {/* SECTION 3.5 — Full-bleed lighting installation image. Emerges out of
          the particle burst as the headline dissolves, then holds pinned on
          screen with a slow parallax push while the viewer walks through it.
          Section is 150vh tall so its 50vh pin runway (scroll 5.0 → 6.5vh)
          gives the image a full beat before the horizontal gallery arrives. */}
      <section className="relative h-[150vh] w-full bg-black">
        <div className="sticky top-0 h-screen w-full overflow-hidden">
          {/* Ultra-realistic contemporary lighting installation image */}
          <div
            className="absolute inset-0"
            style={{
              opacity: tImageIn,
              transform: `scale(${lerp(1.18, 1.02, tImagePan)}) translateY(${lerp(
                4,
                -4,
                tImagePan
              )}%)`,
              transformOrigin: "center 55%",
              willChange: "transform, opacity",
            }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=Ultra%20realistic%20contemporary%20architectural%20interior%20lighting%20installation%2C%20cascade%20of%20slender%20warm%20amber%20brass%20pendant%20lights%20suspended%20at%20varied%20heights%20from%20a%20tall%20plaster%20ceiling%20in%20a%20minimalist%20luxury%20lounge%2C%20soft%20honeyed%20golden%20glow%20pooling%20across%20polished%20microcement%20floor%20and%20muted%20limewash%20walls%2C%20a%20single%20lounge%20chair%20and%20sculptural%20side%20table%20in%20warm%20oak%2C%20late%20afternoon%20natural%20light%20mingling%20with%20artificial%20glow%2C%20deep%20cinematic%20shadows%20and%20delicate%20highlights%2C%20fine%20surface%20texture%2C%20editorial%20interior%20photography%20by%20a%20master%20architectural%20photographer%2C%20high%20detail%2C%20subtle%20atmospheric%20haze%2C%20refined%20contemporary%20Scandinavian%20design%20language&width=2400&height=1350&seq=chapter-two-lighting-installation-01&orientation=landscape"
              alt="Contemporary architectural lighting installation — cascading brass pendants in a warm minimalist lounge"
              className="w-full h-full object-cover object-center"
            />
            {/* Subtle vignette so text overlays remain legible and the image
                sits in mood rather than shouting */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/15 to-black/55 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/20 pointer-events-none" />
          </div>

          {/* Chapter label — quietly floats up as image locks in */}
          <div
            className="absolute top-10 md:top-14 left-0 right-0 flex justify-center pointer-events-none"
            style={{
              opacity: smoothstep(0, 1, clamp01((tImageIn - 0.35) / 0.4)),
              transform: `translateY(${lerp(14, 0, tImageIn)}px)`,
            }}
          >
            <div className="text-[10px] uppercase tracking-[0.4em] text-white/85 flex items-center gap-3">
              <span className="w-8 h-px bg-white/70" />
              Chapter Two · A Room In Practice
              <span className="w-8 h-px bg-white/70" />
            </div>
          </div>

          {/* Bottom caption — arrives after image, drifts on the parallax */}
          <div
            className="absolute bottom-10 md:bottom-14 left-0 right-0 px-8 md:px-14 pointer-events-none"
            style={{
              opacity: smoothstep(0, 1, clamp01((tImageIn - 0.55) / 0.45)) *
                (1 - smoothstep(0.85, 1, tImagePan) * 0.4),
              transform: `translateY(${lerp(20, 0, tImageIn)}px)`,
            }}
          >
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 max-w-[1400px] mx-auto text-white">
              <div className="max-w-md">
                <div className="text-[10px] uppercase tracking-[0.35em] text-white/60 mb-3">
                  Installation No. 07 · Casa Halo
                </div>
                <h3
                  className="font-black uppercase leading-[0.9] tracking-[-0.02em]"
                  style={{ fontSize: "clamp(28px, 3.6vw, 56px)" }}
                >
                  WHERE
                  <br />
                  <span className="italic font-light lowercase tracking-tight text-white/75">
                    the light
                  </span>{" "}
                  LANDS.
                </h3>
              </div>
              <p className="max-w-sm text-sm md:text-[15px] text-white/70 leading-relaxed">
                Seven suspended brass drops, warmed to 2400K, hand-tuned in
                place over four evenings. Late-summer, Lisbon — the room
                learned its own dusk.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — Horizontal project gallery. Tall vertical scroll drives horizontal sweep.
          Section height is tuned so the pin releases right when the sweep completes
          — no dead pinned scroll after the last card lands. */}
      <section className="relative h-[250vh] w-full text-white">
        <div className="sticky top-0 h-screen w-full overflow-hidden bg-black">
          <HorizontalGallery
            progress={tGallerySweep}
            entrance={tGalleryEnter}
          />
        </div>
      </section>

      {/* SECTION 5 — Sketchbook finale. Whole stage transitions dark → paper,
          the intro headline holds on-screen for a long calm beat, and only
          then do the hand-drawn line studies reveal themselves one by one.
          Section height gives the pin (h - 100vh runway) enough scroll room
          for the entire tSketch timeline to complete before releasing. */}
      <section className="relative h-[520vh] w-full">
        <div className="sticky top-0 h-screen w-full overflow-hidden">
          <SketchbookReveal tone={tTone} reveal={tSketch} />
        </div>
      </section>

      {/* CHAPTER 4 — The Studio. Editorial storytelling with team portraits
          and a cinematic studio reel. */}
      <StoryChapter />

      {/* CHAPTER 5 — Book an appointment. Real submission endpoint. */}
      <BookAppointment />

      {/* Footer — warm artistic sign-off */}
      <StudioFooter />
    </div>
  );
}